/**
 * AttributedChronicleBlocks — the IP-respecting amplifier surface (spec §8, §4.8).
 *
 * Rendered below the hero on ChronicleViewPage when sourceType === 'attributed':
 *   - standing attribution label
 *   - Original Project + Support (originator + channels + donate/crowdfund CTA)
 *   - shared discovery classification (solution/problem/issue/outcome)
 *   - local conditions (context tags)
 *   - Sources & citations (primary + references, with role badges)
 *
 * Native chronicles render nothing from here.
 */

import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { ChronicleResponse } from '../../api/chronicles';
import styles from './AttributedChronicleBlocks.module.css';

interface Props {
  chronicle: ChronicleResponse;
}

const SUPPORT_LINK_TYPES = new Set(['donate', 'crowdfund']);
const NON_CHANNEL_LINK_TYPES = new Set(['donate', 'crowdfund', 'news', 'reference']);

export default function AttributedChronicleBlocks({ chronicle }: Props) {
  const { t } = useAppTranslation('pages');

  if (chronicle.sourceType !== 'attributed') return null;

  const sources = chronicle.sources ?? [];
  const primary = sources.find((s) => s.sourceRole === 'primary') ?? null;
  const references = sources
    .filter((s) => s.sourceRole === 'reference')
    .sort((a, b) => a.position - b.position);
  const allSources = [...(primary ? [primary] : []), ...references];

  const links = chronicle.externalLinks ?? [];
  const supportLinks = links.filter((l) => SUPPORT_LINK_TYPES.has(l.linkType));
  const channelLinks = links.filter((l) => !NON_CHANNEL_LINK_TYPES.has(l.linkType));

  const orgName = primary?.sourceOrgName?.trim() || null;

  const classifications = chronicle.classifications ?? [];
  const contexts = chronicle.contexts ?? [];

  const dims: { key: string; label: string }[] = [
    { key: 'solution', label: t('chronicleView.attributed.dimSolution') },
    { key: 'problem', label: t('chronicleView.attributed.dimProblem') },
    { key: 'issue', label: t('chronicleView.attributed.dimIssue') },
    { key: 'outcome', label: t('chronicleView.attributed.dimOutcome') },
  ];

  const showOrigin = primary !== null || supportLinks.length > 0 || channelLinks.length > 0;

  return (
    <div className={styles.wrap}>
      {/* Standing attribution label */}
      <div className={styles.attribLabel}>
        <span className={styles.attribIcon} aria-hidden="true">◆</span>
        <span>
          {orgName
            ? t('chronicleView.attributed.label', { org: orgName })
            : t('chronicleView.attributed.labelNoOrg')}
        </span>
      </div>

      {/* Original Project + Support */}
      {showOrigin && (
        <section className={styles.origin}>
          <div className={styles.originMain}>
            <span className={styles.eyebrow}>{t('chronicleView.attributed.originalProject')}</span>
            {orgName && <div className={styles.orgName}>{orgName}</div>}
            {channelLinks.length > 0 && (
              <div className={styles.channels}>
                {channelLinks.map((l) => (
                  <a
                    key={l.linkGuid}
                    className={styles.channel}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {l.label || l.linkType}
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className={styles.originCtas}>
            {supportLinks.map((l) => (
              <a
                key={l.linkGuid}
                className={styles.ctaSupport}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {'♥ '}
                {l.label || t('chronicleView.attributed.support')}
              </a>
            ))}
            {primary?.sourceUrl && (
              <a
                className={styles.ctaVisit}
                href={primary.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('chronicleView.attributed.visit')}
              </a>
            )}
          </div>
        </section>
      )}

      {/* Discovery / classification */}
      {classifications.length > 0 && (
        <section className={styles.block}>
          <h3 className={styles.blockTitle}>{t('chronicleView.attributed.discoveryTitle')}</h3>
          <div className={styles.dimGrid}>
            {dims.map((d) => {
              const items = classifications.filter((c) => c.dimension === d.key);
              if (items.length === 0) return null;
              return (
                <div key={d.key} className={styles.dimCard}>
                  <div className={`${styles.dimLabel} ${styles['dim_' + d.key] ?? ''}`}>{d.label}</div>
                  <div className={styles.chips}>
                    {items.map((c) => (
                      <span
                        key={c.classificationGuid}
                        className={`${styles.chip} ${c.targetGuid ? styles.chipLinked : styles.chipTerm}`}
                      >
                        {c.valueKey || c.note || '—'}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Local conditions moved to the shared ChronicleLocationBlock on About
          (DEV-1321: conditions live on About, shown for all chronicles). */}

      {/* Sources & citations */}
      {allSources.length > 0 && (
        <section className={styles.block}>
          <h3 className={styles.blockTitle}>{t('chronicleView.attributed.sourcesTitle')}</h3>
          <ol className={styles.sourceList}>
            {allSources.map((s, i) => (
              <li key={s.sourceGuid} className={styles.sourceItem}>
                <span className={styles.sourceNum}>{i + 1}</span>
                <div className={styles.sourceMeta}>
                  <div className={styles.sourceTitle}>
                    {s.sourceTitle || s.sourceOrgName || s.sourceUrl || '—'}
                  </div>
                  {s.sourceUrl && (
                    <a
                      className={styles.sourceUrl}
                      href={s.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {s.sourceUrl}
                    </a>
                  )}
                </div>
                <span
                  className={`${styles.role} ${
                    s.sourceRole === 'primary' ? styles.rolePrimary : styles.roleReference
                  }`}
                >
                  {s.sourceRole === 'primary'
                    ? t('chronicleView.attributed.rolePrimary')
                    : t('chronicleView.attributed.roleReference')}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
